import React, { RefObject, useEffect, useRef, useState } from 'react';
import Styled from './styled';
import { ChevronRight } from '../Icon';

export type ProductCardProps = {
    /** Product card title. */
    title: string;
    /** Product card subtext. */
    subtext?: string;
    /** Click handler for the product card. */
    onClick?: () => void;
    /** Optional, text for price if the price is marked down */
    markdownPrice?: string;
    /** Optional, product price */
    price?: string;
    /** Optional, product image url */
    imageUrl?: string;
    /** Optional, disabled state (removes on-hover/on-click) */
    disabled?: boolean;
};

const truncateText = (text: string, element: HTMLElement, lines: number): string => {
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const maxHeight = lineHeight * lines;

    let truncatedText = text;
    let displayText = text;
    element.innerText = displayText;

    while (element.scrollHeight > maxHeight && truncatedText.length > 0) {
        truncatedText = truncatedText.slice(0, -1);
        displayText = `${truncatedText}...`;
        element.innerText = displayText;
    }

    return displayText;
};

const useTruncatedText = (text: string, ref: RefObject<HTMLElement>, lines: number) => {
    const [truncatedText, setTruncatedText] = useState(text);

    useEffect(() => {
        if (!ref.current) return;

        const element = ref.current;

        const performTruncation = () => {
            const displayText = truncateText(text, element, lines);
            setTruncatedText(displayText);
        };

        performTruncation();

        //Check for changes/resizing (collapsed, page refresh, etc.)
        const resizeObserver = new ResizeObserver(performTruncation);
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [text, ref, lines]);

    return truncatedText;
};

/**
 * ProductCard component that displays a product image, url, price (markdown) and title
 * 
 * @param {ProductCardProps} props - The properties to configure the ProductCard component.
 * @returns {JSX.Element | null} The rendered ProductCard component or null if not visible.
 * 
 * TODO: Extend Currency Support beyond US Dollars
 */
export const ProductCard: React.FC<ProductCardProps> = ({
    title,
    subtext,
    onClick,
    markdownPrice,
    price,
    imageUrl,
    disabled,
}) => {
    const titleRef = useRef<HTMLHeadingElement>(null);
    const subtextRef = useRef<HTMLParagraphElement>(null);

    const truncatedTitle = useTruncatedText(title || 'Product Details Unavailable', titleRef, 1);
    const truncatedSubtext = useTruncatedText(subtext || '', subtextRef, 2);

    return (
        <Styled.ProductCard
            onClick={onClick} disabled={disabled}
            aria-label={truncatedTitle}
            tabIndex={0}
            role='button'
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); // Prevent default scrolling for Space key
                    onClick?.();
                }
            }}

        >
            {imageUrl && <Styled.ProductImage src={imageUrl} alt='product-image' />}
            <Styled.ContentWrapper>
                <Styled.Title ref={titleRef}>{truncatedTitle}</Styled.Title>
                {subtext && <Styled.Subtext ref={subtextRef}>{truncatedSubtext}</Styled.Subtext>}
                <Styled.PriceWrapper>
                    {price && <Styled.Price>${price}</Styled.Price>}
                    {markdownPrice && <Styled.MarkdownPrice>${markdownPrice}</Styled.MarkdownPrice>}
                </Styled.PriceWrapper>
            </Styled.ContentWrapper>
            <Styled.Icon><ChevronRight /></Styled.Icon>
        </Styled.ProductCard>
    );
};
